import * as React from "react";
import { Outlet, createRootRoute } from "@tanstack/react-router";

import { MainLayout } from "@/components/layout/main-layout";
import { NotFound } from "@/components/not-found";

import { Toaster } from "@/components/ui/sonner";
import { trySilentSignIn } from "@/lib/auth";
import {
  isAuthenticated,
  redirectToLogin,
} from "@/hooks/services/workchestra/handler/auth";

import { userManager } from "@/lib/oidc-config";
import { SidebarProvider } from "@/components/ui/sidebar";
import useEnsureUserProfile from "@/hooks/use-ensure-user-profile";
import { useUserProfileStore } from "@/stores/user-profile-store";
import { fetchUserProfile } from "@/lib/auth";

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
  // Check authentication before loading any route
  beforeLoad: async ({ location }) => {
    const qs = new URLSearchParams(location.search);
    const isLogoutCallback = qs.has("state") && !qs.has("code");
    if (isLogoutCallback) {
      return; // let signoutRedirectCallback run first
    }

    // Skip auth check for logout route so the logout flow can proceed
    if (
      location.pathname === "/logout" ||
      location.pathname.endsWith("/logout")
    ) {
      return;
    }

    // Skip auth check for callback route (OIDC redirect)
    if (
      location.pathname === "/callback" ||
      location.pathname.endsWith("/callback")
    ) {
      return;
    }

    const isAuth = await isAuthenticated();

    if (!isAuth) {
      const recovered = await trySilentSignIn();
      if (recovered) {
        return;
      }
      
      await redirectToLogin();
      return;
    }

    const { setUserProfile } = useUserProfileStore.getState();
    const profile = await fetchUserProfile();
    setUserProfile(profile);
    return;
  },
});

function RootComponent() {
  useEnsureUserProfile();
  React.useEffect(() => {
    (async () => {
      const qs = new URLSearchParams(window.location.search);
      const isLogoutCallback = qs.has("state") && !qs.has("code"); // typical logout return
      if (isLogoutCallback) {
        try {
          await userManager.signoutRedirectCallback();
        } catch {}
      }
      const postLogoutReturnUrl = sessionStorage.getItem("postLogoutReturnUrl");
      if (postLogoutReturnUrl) {
        sessionStorage.removeItem("postLogoutReturnUrl");
        window.location.replace(postLogoutReturnUrl);
      }
    })();
  }, []);

  return (
    <React.Fragment>
      <SidebarProvider>
        <MainLayout>
          <Outlet />
          <Toaster />
        </MainLayout>
      </SidebarProvider>
    </React.Fragment>
  );
}
